import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';

import { KaseApi } from '@/api';
import { ArrestsToolApi, IArrestToolRow } from '@/api/api.arrests';
import { KaseFileApi } from '@/api/api.kase.file';
import { DataGrid, Page, PageHeader } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { formatSortParam } from '@/components/TailwindComponents';
import { LocalStorageItems, LocalStoragePrefix } from '@/constants';
import { useFormConfigLoader } from '@/hooks';
import {
  FileOrigin,
  IFile,
  IOffenseOption,
  IOffenseOptionItem,
} from '@/models';
import { buildArrestReportDefinition } from '@/pages/Kases/KaseArrests/reports/arrestReport/buildArrestReportDefinition';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { uploadFiles } from '@/services';
import { isRmsCasesViewOnlyUser } from '@/services/service.kase';
import {
  useAgencyStore,
  ConfirmTypes,
  useToastStore,
  useUserStore,
} from '@/store';
import { getLocalStorageItem } from '@/utils';

import { ArrestsToolArrestModal } from './ArrestsToolArrestModal';
import { arrestsToolDataGridCols } from './data';

// Server-side filter on arresteeStatus matches the stored code (e.g. "06").
// Translate label-based input into the matching code(s) so users can type
// "Police" or "Police Bail" and have it find rows with code "06".
const translateArresteeStatusFilter = (
  filterStr: string,
  options: IOffenseOption[],
): string => {
  if (!filterStr) return filterStr;

  let parsed: { items?: Array<Record<string, unknown>> };
  try {
    parsed = JSON.parse(filterStr);
  } catch {
    return filterStr;
  }

  if (!Array.isArray(parsed?.items) || parsed.items.length === 0) {
    return filterStr;
  }

  const statusMap = getOffenseOptionItems(
    'arrestStatus',
    options ?? [],
  ) as unknown as Map<string, IOffenseOptionItem>;
  if (!statusMap || statusMap.size === 0) return filterStr;

  parsed.items = parsed.items.map((item) => {
    const operator = String(item.operator ?? '');
    if (
      item.field !== 'arresteeStatus' ||
      item.value == null ||
      item.value === '' ||
      operator === 'isEmpty' ||
      operator === 'isNotEmpty' ||
      operator === 'isAnyOf'
    ) {
      return item;
    }

    const userValue = String(item.value).toLowerCase();
    const allCodes: string[] = [];
    const matchingCodes: string[] = [];

    statusMap.forEach((opt, code) => {
      allCodes.push(code);
      const label = (opt.label ?? '').toLowerCase();
      const codeLower = code.toLowerCase();
      let matches = false;
      switch (operator) {
        case 'equals':
        case 'is':
        case 'doesNotEqual':
          // Strict equality compares against the full displayed label
          // ("02 = Appearance Ticket"). Matching by code alone would let
          // input "02" return every row whose status starts with that code.
          matches = label === userValue;
          break;
        case 'contains':
        case 'doesNotContain':
          matches = label.includes(userValue) || codeLower.includes(userValue);
          break;
        case 'startsWith':
          matches =
            label.startsWith(userValue) || codeLower.startsWith(userValue);
          break;
        case 'endsWith':
          matches = label.endsWith(userValue) || codeLower.endsWith(userValue);
          break;
        default:
          matches = label.includes(userValue) || codeLower.includes(userValue);
      }
      if (matches) matchingCodes.push(code);
    });

    const negation =
      operator === 'doesNotEqual' || operator === 'doesNotContain';
    const codesToSend = negation
      ? allCodes.filter((c) => !matchingCodes.includes(c))
      : matchingCodes;

    if (codesToSend.length === 0) {
      // No label/code matches the input — send a sentinel so backend returns 0.
      return {
        field: 'arresteeStatus',
        operator: 'isAnyOf',
        value: ['__NO_MATCH__'],
      };
    }

    return {
      field: 'arresteeStatus',
      operator: 'isAnyOf',
      value: codesToSend,
    };
  });

  return JSON.stringify(parsed);
};

const ArrestsToolDataGrid = () => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const navigate = useNavigate();

  const { allOptions, filteredOptions, loading } = useFormConfigLoader({
    optionsName: 'OFFENSE',
    category: 'ARRESTS',
  });

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `arrests-tool-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return arrestsToolDataGridCols;
      }

      return mergeColumnSettings(arrestsToolDataGridCols, savedSettings);
    },
  );

  const { currentAgency, fetchAgency } = useAgencyStore();
  const [isPrinting, setIsPrinting] = useState(false);
  const [printingArresteeId, setPrintingArresteeId] = useState<string | null>(
    null,
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentArrestee, setCurrentArrestee] = useState<IArrestToolRow | null>(
    null,
  );
  const [initialEditMode, setInitialEditMode] = useState(false);
  const [visibleRows, setVisibleRows] = useState<IArrestToolRow[]>([]);
  const lastFetchRef = useRef(Date.now());

  const filteredOptionsRef = useRef(filteredOptions);
  useEffect(() => {
    filteredOptionsRef.current = filteredOptions;
  }, [filteredOptions]);

  const handleView = useCallback((row: IArrestToolRow) => {
    setInitialEditMode(false);
    setCurrentArrestee(row);
  }, []);

  const handleEdit = useCallback((row: IArrestToolRow) => {
    setInitialEditMode(true);
    setCurrentArrestee(row);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (currentArrestee) return; // don't thrash while user is editing
      if (Date.now() - lastFetchRef.current < 10_000) return; // 10s throttle
      lastFetchRef.current = Date.now();
      setRefreshKey((k) => k + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [currentArrestee]);

  const handleExport = useCallback(
    async (row: IArrestToolRow) => {
      if (!row.kase?._id || !row._id) return;
      try {
        await KaseApi.initiateFingerprintScan(row.kase._id, row._id);
        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Export to DataWorks triggered successfully',
        });
        setRefreshKey((k) => k + 1);
      } catch (err: any) {
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: err?.message || 'Failed to trigger export to DataWorks',
        });
      }
    },
    [updateToast],
  );

  const handlePrint = useCallback(
    async (row: IArrestToolRow) => {
      if (isPrinting) return;

      const kaseId = row.kase?._id;
      if (!kaseId) {
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: 'No case associated with this arrest',
        });
        return;
      }

      setIsPrinting(true);
      setPrintingArresteeId(row._id);

      try {
        const kaseRes = await KaseApi.getOne(kaseId);
        const kaseData = kaseRes?.data;
        if (!kaseData) {
          throw new Error('Failed to load case data');
        }

        const agencyId = localStorage.getItem(
          LocalStorageItems.CurrentAgencyId,
        );
        let agencyData = currentAgency;
        if (!agencyData?._id && agencyId) {
          agencyData = (await fetchAgency(agencyId)) ?? null;
        }

        const masterNameId = row.masterName;
        const arresteeRes = await KaseApi.getArrestee(kaseId, masterNameId);
        const arrestee = arresteeRes?.data;
        if (!arrestee) {
          throw new Error('Failed to load arrestee data');
        }

        const arresteeMasterNameId = arrestee.masterName?._id?.toString();
        const [associatedPersonsRes, narrativesRes] = await Promise.all([
          KaseApi.getLinkedMasterNames(kaseId, {}),
          arresteeMasterNameId
            ? KaseApi.getArresteeNarratives(kaseId, arresteeMasterNameId)
            : Promise.resolve({ data: [] }),
        ]);

        const associatedPersons = associatedPersonsRes?.data || [];
        const narratives = narrativesRes?.data || [];

        const printedAt = new Date();
        const fileName = `${kaseData.number} - ${
          arrestee.arrestId || arrestee.number
        } - Arrest Report - Printed on ${moment(printedAt).format(
          'MM-DD-YYYY HH:mm',
        )}.pdf`;

        const pdfDef = await buildArrestReportDefinition({
          ctx: {
            kase: kaseData,
            agency: agencyData as any,
            user,
            arrestee,
            associatedPersons,
            narratives,
            printedAt,
            offenseOptions: allOptions,
          },
        });

        const pdfMakeModule = await import('pdfmake/build/pdfmake');
        const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
        const pdfMakeInstance = (pdfMakeModule as any).default || pdfMakeModule;
        const fontsInstance = (pdfFontsModule as any).default || pdfFontsModule;

        if (pdfMakeInstance && fontsInstance) {
          pdfMakeInstance.vfs = fontsInstance.pdfMake?.vfs || fontsInstance;
        }

        const createPdf = pdfMakeInstance.createPdf || pdfMakeInstance;
        const pdfDocGenerator = createPdf(pdfDef);

        pdfDocGenerator.print();

        const blob: Blob = await new Promise((resolve, reject) => {
          try {
            pdfDocGenerator.getBlob((b: Blob) => resolve(b));
          } catch (e) {
            reject(e);
          }
        });

        const file = new File([blob], fileName, { type: 'application/pdf' });
        const uploadedFiles = await uploadFiles([
          {
            name: file.name,
            size: file.size,
            mimeType: file.type,
            origin: FileOrigin.ARRESTEES,
            file,
          },
        ]);

        const existingFiles =
          kaseData?.files?.map((f: IFile) => ({
            ...f,
            uploadedBy: undefined,
          })) ?? [];
        const response = await KaseFileApi.updateKaseFiles(kaseId, {
          files: [...uploadedFiles, ...existingFiles],
        });

        if (response.status !== 200) {
          throw new Error(`File Upload Failed: ${response.statusText}`);
        }

        updateToast({
          open: true,
          type: ConfirmTypes.SUCCESS,
          message: 'Arrest Report saved to case successfully!',
        });
      } catch (err: any) {
        updateToast({
          open: true,
          type: ConfirmTypes.ERROR,
          message: err?.message || 'Failed to print Arrest Report',
        });
      } finally {
        setIsPrinting(false);
        setPrintingArresteeId(null);
      }
    },
    [isPrinting, currentAgency, fetchAgency, user, updateToast],
  );

  const fetchArrestsList = useCallback(
    async (
      page: number,
      limit: number,
      filter: string,
      sort?: string,
    ): Promise<{ rows: IArrestToolRow[]; totalCount: number }> => {
      lastFetchRef.current = Date.now();
      try {
        const response = await ArrestsToolApi.list({
          page,
          limit,
          filter: translateArresteeStatusFilter(
            filter,
            filteredOptionsRef.current ?? [],
          ),
          sort: formatSortParam(sort, { createdAt: -1 }),
        });

        const { data } = response;
        setVisibleRows(data.docs);
        return { rows: data.docs, totalCount: data.totalDocs };
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [updateToast],
  );

  const columns = useDataGridColumns({
    type: 'arrestsTool',
    columnSettings,
    navigate,
    filteredOptions,
    isReadOnly: isRmsCasesViewOnlyUser(user),
    actionMethods: {
      onPrint: handlePrint,
      onView: handleView,
      onEdit: handleEdit,
      onExport: handleExport,
      isPrinting,
      printingArresteeId,
    },
  });

  if (loading) {
    return null;
  }

  return (
    <Page title="Arrests">
      <PageHeader title="Arrests" sx={{ borderBottom: 'none' }} />
      <DataGrid
        key={refreshKey}
        name="arrests-tool"
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        fetchData={fetchArrestsList}
        onRowClick={(params) => handleView(params.row)}
      />
      <ArrestsToolArrestModal
        open={!!currentArrestee}
        row={currentArrestee}
        initialEditMode={initialEditMode}
        onClose={() => {
          setCurrentArrestee(null);
          setRefreshKey((k) => k + 1);
        }}
        navigation={(() => {
          if (!currentArrestee) return undefined;
          const idx = visibleRows.findIndex(
            (r) => r._id === currentArrestee._id,
          );
          if (idx < 0) return undefined;
          const goTo = (nextIdx: number) => {
            setInitialEditMode(false);
            setCurrentArrestee(visibleRows[nextIdx]);
          };
          return {
            canPrev: idx > 0,
            canNext: idx < visibleRows.length - 1,
            onPrev: () => goTo(idx - 1),
            onNext: () => goTo(idx + 1),
          };
        })()}
      />
    </Page>
  );
};

export default ArrestsToolDataGrid;
